import React from 'react';
import { motion } from 'framer-motion';
import { MicIcon, SquareIcon } from 'lucide-react';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Deterministic bar heights so the waveform reads as a real voice envelope. */
const BARS = [0.35, 0.6, 0.85, 0.5, 0.95, 0.7, 0.4, 0.8, 1, 0.55, 0.3, 0.75, 0.9, 0.45, 0.65, 0.85, 0.5, 0.35, 0.7, 0.95, 0.6, 0.4, 0.8, 0.55, 0.3, 0.7, 0.9, 0.5, 0.4, 0.6];
interface VoiceCaptureProps {
  /** Live transcription text, typed in as the customer speaks. */
  transcript: string;
  /** Still listening — the waveform animates and the dot pulses. */
  listening: boolean;
  /** Right-to-left transcript (Arabic). */
  rtl?: boolean;
}

/**
 * The composer while Talab is listening: a capture bar with a live waveform
 * and the speech being transcribed into the input in real time.
 */
export function VoiceCapture({
  transcript,
  listening,
  rtl = false
}: VoiceCaptureProps) {
  return <div className="relative" data-id="element-2415" data-mp="2zdjh3wt80wdh">
      <motion.div className="absolute -inset-[3px] rounded-[26px] bg-gradient-to-r from-primary-indigo/30 to-ai-purple/30 blur-md" animate={{
      opacity: listening ? [0.55, 1, 0.55] : 0.6
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} aria-hidden="true" data-id="element-2416" data-mp="2zgp496iaho6k" />

      <div className="relative flex items-center gap-3 bg-white rounded-full border border-primary-indigo/20 pl-3 pr-1.5 py-1.5 shadow-[0_8px_24px_rgb(67,56,202,0.14)]" data-id="element-2417" data-mp="131tmak0m1no">
        {/* Listening indicator */}
        <span className="flex items-center gap-1.5 shrink-0" data-id="element-2418" data-mp="39kpjr7k63iti">
          <motion.span className="w-6 h-6 rounded-full bg-primary-indigo flex items-center justify-center" animate={{
          scale: listening ? [1, 1.08, 1] : 1
        }} transition={{
          duration: 1.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2419" data-mp="39l7luktigr9c">
            <MicIcon className="w-3 h-3 text-white" data-id="element-2420" data-mp="2lu2aahulsl4u" />
          </motion.span>
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-primary-indigo" data-id="element-2421" data-mp="2qtomqp2lxxcl">
            {listening ? 'Listening' : 'Transcribed'}
          </span>
        </span>

        {/* Waveform */}
        <div className="flex items-center gap-[2px] h-5 shrink-0" aria-hidden="true" data-id="element-2422" data-mp="2dv7btotdrpe3">
          {BARS.slice(0, 22).map((height, index) => <motion.span key={index} className="w-[2px] rounded-full bg-primary-indigo/45" animate={listening ? {
          height: [4, 20 * height, 6, 16 * height, 4]
        } : {
          height: 4
        }} transition={{
          duration: 1.1,
          repeat: listening ? Infinity : 0,
          ease: 'easeInOut',
          delay: index * 0.045
        }} data-id="element-2423" data-mp="2sik4rylioxei" data-mpi={index} />)}
        </div>

        {/* Live transcription */}
        <div className={`flex-1 min-w-0 text-[13px] truncate ${rtl ? 'text-right' : 'text-left'}`} dir={rtl ? 'rtl' : 'ltr'} data-id="element-2424" data-mp="3l6y0u8t1pjnf">
          {transcript ? <span className="text-dark-navy" data-id="element-2425" data-mp="3nrvzlp3fn3t7">{transcript}</span> : <span className="text-slate-300" data-id="element-2426" data-mp="3sries42tmu4n">{rtl ? 'جارٍ الاستماع…' : 'Listening…'}</span>}
          {listening && <motion.span className="inline-block w-[1.5px] h-[14px] mx-0.5 -mb-[2px] bg-primary-indigo align-middle" animate={{
          opacity: [1, 1, 0, 0]
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-2427" data-mp="3esirazr1ola2" />}
        </div>

        <motion.span className="w-8 h-8 rounded-full bg-primary-indigo flex items-center justify-center shrink-0" animate={{
        scale: listening ? 1 : [1, 0.94, 1]
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-2428" data-mp="fcvjzuldpnmd">
          <SquareIcon className="w-3 h-3 text-white" fill="currentColor" data-id="element-2429" data-mp="5kpq2fbkgw4j" />
        </motion.span>
      </div>
    </div>;
}